import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Bath,
    Bed,
    Building2,
    Car,
    Database,
    ExternalLink,
    Eye,
    Home,
    Loader2,
    Map as MapIcon,
    MapPin,
    Ruler,
    Search,
    SlidersHorizontal,
    Trees,
    UserRound,
    X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { searchPropertyProfiles } from "../../../api/collingsData";
import ManualSearchOverlay from "../components/ManualSearchOverlay";
import PropertyMapView, { KIND_COLORS, KIND_LABELS } from "../components/PropertyMapView";

/**
 * Public Property Search (`/collings-data-properties`).
 *
 * Christian Gravias, Monday 12573204628 (12 Oct 2026): "Now that we have
 * sorted all Homer data into our CRM please create the Public Property Search
 * where all the data can be accessed in this section. Please make our page
 * look exactly like this. Just dont copy agents." The header copies Homer's
 * own search (title, All / Buy / Rent / Sold / Off Market, Include surrounding
 * areas, search box with Show Filters, Search button). The filters keep our
 * Manual Search panel, minus its suburb box, since the search bar above
 * already takes the suburb.
 *
 * Every card is one property's permanent profile
 * (suburb_intelligence.property_profiles): our scraped data, Homer's campaigns
 * and the office's spreadsheets for that address, together. "I want agents to
 * search under All Properties and find everything together ... The search
 * categories should relate to the property or listing status ... Where the
 * information originated should be recorded internally rather than
 * determining which tab it appears in" (Christian, 26 Oct 2026), so there is
 * no Collings Data or On file tab any more. Tabs, filters and paging are all
 * answered by the backend.
 */

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || "";
const PAGE_SIZE = 30;
// Remembers the last search so "Back" from a property lands on the same results.
// v2: the source tabs (collings, on_file) a v1 search may hold are gone.
const CACHE_KEY = "publicPropertySearchState_v2";
const HERO_IMAGE =
    "https://images.unsplash.com/photo-1605146769289-440113cc3d00?auto=format&fit=crop&w=2070&q=80";

const TABS = [
    { id: "all", label: "All Properties" },
    { id: "buy", label: "For Sale" },
    { id: "rent", label: "For Rent" },
    { id: "leased", label: "Leased" },
    { id: "sold", label: "Sold" },
    { id: "off_market", label: "Off Market" },
];

// Every address comes from G-NAF, whose licence (CC BY 4.0) asks for this credit.
const GNAF_CREDIT =
    "Incorporates or developed using G-NAF © Geoscape Australia licensed by the Commonwealth of Australia " +
    "under the Open Geo-coded National Address File (G-NAF) End User Licence Agreement.";

const SORTS = [
    { id: "newest", label: "Newest" },
    { id: "price_high", label: "Price (high to low)" },
    { id: "price_low", label: "Price (low to high)" },
    { id: "suburb", label: "Suburb A-Z" },
];

const DEFAULT_ADV_FILTERS = {
    propertyTypes: ["all"],
    priceRange: { min: "Any", max: "Any" },
    bedrooms: { min: "Any", max: "Any" },
    useBedroomRange: false,
    bathrooms: "Any",
    carSpaces: "Any",
    landSize: { min: "Any", max: "Any" },
    saleMethod: "All",
    excludeUnderContract: false,
    targetYield: "Any",
    capitalGrowth: "Any",
    showWithPriceOnly: false,
};

const TYPE_ICONS = { house: Home, townhouse: Home, apartment: Building2, land: Trees };

function hasCoords(p) {
    const lat = Number(p.latitude);
    const lng = Number(p.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) && p.latitude != null && p.longitude != null;
}

/** Search bar + Manual Search panel -> query params for the backend. */
function buildParams({ query, tab, surrounding, adv, sort, page }) {
    const params = { tab, sort, page, page_size: PAGE_SIZE };
    if (query.trim()) params.q = query.trim();
    if (surrounding) params.surrounding = 1;
    if (!adv.propertyTypes.includes("all")) params.property_type = adv.propertyTypes.join(",");
    if (adv.priceRange.min !== "Any") params.price_min = adv.priceRange.min;
    if (adv.priceRange.max !== "Any") params.price_max = adv.priceRange.max;
    if (adv.bedrooms.min !== "Any") params.beds_min = adv.bedrooms.min;
    if (adv.bedrooms.max !== "Any") params.beds_max = adv.bedrooms.max;
    if (adv.bathrooms !== "Any") params.baths_min = adv.bathrooms;
    if (adv.carSpaces !== "Any") params.cars_min = adv.carSpaces;
    if (adv.landSize.min !== "Any") params.land_min = adv.landSize.min;
    if (adv.landSize.max !== "Any") params.land_max = adv.landSize.max;
    return params;
}

function readCache() {
    try {
        return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
    } catch {
        return null;
    }
}

const CollingsDataPropertiesPage = () => {
    const navigate = useNavigate();
    const cached = useRef(readCache()).current;

    // What the search bar and panel currently hold.
    const [query, setQuery] = useState(cached?.query || "");
    const [tab, setTab] = useState(cached?.tab || "all");
    const [surrounding, setSurrounding] = useState(!!cached?.surrounding);
    const [advFilters, setAdvFilters] = useState(cached?.adv || DEFAULT_ADV_FILTERS);
    const [sort, setSort] = useState(cached?.sort || "newest");
    // What the results on screen were searched with (chips, tabs, paging).
    const [applied, setApplied] = useState(
        cached?.applied || { query: "", surrounding: false, adv: DEFAULT_ADV_FILTERS }
    );

    const [results, setResults] = useState([]);
    const [count, setCount] = useState(0);
    const [totals, setTotals] = useState(null);
    const [databaseTotal, setDatabaseTotal] = useState(null);
    const [lastSynced, setLastSynced] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({ suburb: "", state: "" }); // the panel's (hidden) suburb box
    const [showMap, setShowMap] = useState(!!cached?.showMap);
    const [selectedId, setSelectedId] = useState(null);
    const [fitTrigger] = useState(0);

    const loadIdRef = useRef(0);
    const listRef = useRef(null);
    const sentinelRef = useRef(null);
    const cardRefs = useRef({});
    const pendingScrollRef = useRef(cached?.scrollY || null);

    const fetchPage = useCallback(async (search, pageNo) => {
        const myLoad = pageNo === 1 ? ++loadIdRef.current : loadIdRef.current;
        if (pageNo === 1) {
            setLoading(true);
            setError(null);
            setSelectedId(null);
        } else {
            setLoadingMore(true);
        }
        try {
            const res = await searchPropertyProfiles(buildParams({ ...search, page: pageNo }));
            if (myLoad !== loadIdRef.current) return;
            const data = res.data || {};
            setResults((prev) => (pageNo === 1 ? data.results || [] : [...prev, ...(data.results || [])]));
            setCount(data.count || 0);
            setTotals(data.totals || null);
            setDatabaseTotal(data.database_total ?? null);
            setLastSynced(data.last_synced_at || null);
            setHasMore(!!data.has_more);
            setPage(pageNo);
        } catch (err) {
            if (myLoad !== loadIdRef.current) return;
            const msg = err.response?.data?.detail || err.message || "Failed to load properties";
            if (pageNo === 1) {
                setError(msg);
                setResults([]);
            }
            toast.error(msg);
        } finally {
            if (myLoad === loadIdRef.current) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, []);

    const runSearch = useCallback(
        (overrides = {}) => {
            const search = {
                query: overrides.query ?? query,
                tab: overrides.tab ?? tab,
                surrounding: overrides.surrounding ?? surrounding,
                adv: overrides.adv ?? advFilters,
                sort: overrides.sort ?? sort,
            };
            setApplied({ query: search.query, surrounding: search.surrounding, adv: search.adv });
            fetchPage(search, 1);
        },
        [query, tab, surrounding, advFilters, sort, fetchPage]
    );

    // First load: the remembered search when coming back, else everything.
    useEffect(() => {
        if (cached?.applied) {
            const a = cached.applied;
            fetchPage({ query: a.query, tab: cached.tab || "all", surrounding: a.surrounding, adv: a.adv, sort: cached.sort || "newest" }, 1);
        } else {
            fetchPage({ query: "", tab: "all", surrounding: false, adv: DEFAULT_ADV_FILTERS, sort: "newest" }, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Put the list back where it was after "Back".
    useEffect(() => {
        if (!loading && pendingScrollRef.current != null && results.length) {
            const y = pendingScrollRef.current;
            pendingScrollRef.current = null;
            requestAnimationFrame(() => listRef.current?.scrollTo(0, y));
        }
    }, [loading, results]);

    const saveState = useCallback(() => {
        try {
            sessionStorage.setItem(
                CACHE_KEY,
                JSON.stringify({ query, tab, surrounding, adv: advFilters, sort, applied, showMap, scrollY: listRef.current?.scrollTop || 0 })
            );
        } catch {
            /* storage unavailable: Back just starts fresh */
        }
    }, [query, tab, surrounding, advFilters, sort, applied, showMap]);

    const loadMore = useCallback(() => {
        if (!hasMore || loading || loadingMore) return;
        fetchPage({ query: applied.query, tab, surrounding: applied.surrounding, adv: applied.adv, sort }, page + 1);
    }, [hasMore, loading, loadingMore, fetchPage, applied, tab, sort, page]);

    // Infinite scroll inside the results column.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMore();
            },
            { root: listRef.current, rootMargin: "400px" }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loadMore]);

    const handleTab = (id) => {
        setTab(id);
        fetchPage({ query: applied.query, tab: id, surrounding: applied.surrounding, adv: applied.adv, sort }, 1);
    };

    const handleSurrounding = () => {
        const next = !surrounding;
        setSurrounding(next);
        if (applied.query.trim()) runSearch({ surrounding: next });
    };

    const handleSort = (e) => {
        setSort(e.target.value);
        fetchPage({ query: applied.query, tab, surrounding: applied.surrounding, adv: applied.adv, sort: e.target.value }, 1);
    };

    const handleClear = () => {
        setQuery("");
        setSurrounding(false);
        setAdvFilters(DEFAULT_ADV_FILTERS);
        setTab("all");
        runSearch({ query: "", surrounding: false, adv: DEFAULT_ADV_FILTERS, tab: "all" });
    };

    // Removable chips for the filters the results were searched with.
    const activeFilterChips = useMemo(() => {
        const a = applied.adv;
        const chips = [];
        const fmtMoney = (v) => {
            const n = Number(v);
            return n >= 1000000 ? `$${n / 1000000}M` : `$${Math.round(n / 1000)}k`;
        };
        const reapply = (next) => {
            setAdvFilters(next);
            runSearch({ query: applied.query, surrounding: applied.surrounding, adv: next });
        };
        if (!a.propertyTypes.includes("all")) {
            a.propertyTypes.forEach((t) =>
                chips.push({
                    key: `type-${t}`,
                    label: t,
                    clear: () => {
                        const rest = a.propertyTypes.filter((x) => x !== t);
                        reapply({ ...a, propertyTypes: rest.length ? rest : ["all"] });
                    },
                })
            );
        }
        if (a.priceRange.min !== "Any" || a.priceRange.max !== "Any") {
            const { min, max } = a.priceRange;
            const label =
                min !== "Any" && max !== "Any" ? `${fmtMoney(min)} to ${fmtMoney(max)}`
                : min !== "Any" ? `${fmtMoney(min)}+` : `Up to ${fmtMoney(max)}`;
            chips.push({ key: "price", label, clear: () => reapply({ ...a, priceRange: { min: "Any", max: "Any" } }) });
        }
        if (a.bedrooms.min !== "Any" || a.bedrooms.max !== "Any") {
            const { min, max } = a.bedrooms;
            const label =
                min !== "Any" && max !== "Any" ? `${min} to ${max} beds`
                : min !== "Any" ? `${min}+ beds` : `Up to ${max} beds`;
            chips.push({ key: "beds", label, clear: () => reapply({ ...a, bedrooms: { min: "Any", max: "Any" } }) });
        }
        if (a.bathrooms !== "Any")
            chips.push({ key: "baths", label: `${a.bathrooms}+ bath`, clear: () => reapply({ ...a, bathrooms: "Any" }) });
        if (a.carSpaces !== "Any")
            chips.push({ key: "cars", label: `${a.carSpaces}+ car`, clear: () => reapply({ ...a, carSpaces: "Any" }) });
        if (a.landSize.min !== "Any" || a.landSize.max !== "Any") {
            const { min, max } = a.landSize;
            const label =
                min !== "Any" && max !== "Any" ? `${min} to ${max} m²`
                : min !== "Any" ? `${min}+ m²` : `Up to ${max} m²`;
            chips.push({ key: "land", label, clear: () => reapply({ ...a, landSize: { min: "Any", max: "Any" } }) });
        }
        return chips;
    }, [applied, runSearch]);

    const mapProperties = useMemo(() => results.filter(hasCoords), [results]);

    const openDetail = (p) => {
        saveState();
        navigate(p.url);
    };

    // Clicking anywhere on a card opens the property. It used to only highlight
    // it on the map, which read as "Its not letting me click to search any
    // properties" (Christian Gravias, Monday 12573204628, 14 Oct 2026).
    const handleCardClick = (p) => openDetail(p);

    const handleMarkerSelect = useCallback((p) => {
        setSelectedId(p.id);
        const el = cardRefs.current[p.id];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, []);

    const tabLabel = TABS.find((t) => t.id === tab)?.label || "All";

    return (
        <div className="min-h-screen bg-white text-[#36423F] -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6 w-auto max-w-full overflow-x-hidden flex flex-col">
            {/* Hero: Homer's "Start your property search here", without Agents */}
            <div
                className="relative bg-cover bg-center px-4 sm:px-6 lg:px-8 py-6 sm:py-10"
                style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
            >
                <div className="absolute inset-0 bg-black/30" />
                <div className="relative mx-auto max-w-5xl rounded-xl bg-[#3b3f45]/75 px-4 sm:px-8 pt-5 sm:pt-6 pb-2 backdrop-blur-[2px]">
                    <h1 className="text-center text-2xl sm:text-4xl font-bold text-white">Start your property search here</h1>

                    <div className="mt-4 sm:mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div className="flex items-center gap-1 overflow-x-auto">
                            {TABS.map((t) => {
                                const active = tab === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => handleTab(t.id)}
                                        className={`shrink-0 px-2 sm:px-5 pb-2 text-[15px] sm:text-lg font-semibold border-b-[3px] transition-colors ${
                                            active ? "border-[#55C5D0] text-white" : "border-transparent text-white/85 hover:text-white"
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                        <label className="flex shrink-0 cursor-pointer items-center gap-2 pb-2 text-sm font-semibold text-white">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={surrounding}
                                onClick={handleSurrounding}
                                className={`relative h-5 w-9 rounded-full transition-colors ${surrounding ? "bg-[#55C5D0]" : "bg-white/40"}`}
                            >
                                <span
                                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                                        surrounding ? "left-[18px]" : "left-0.5"
                                    }`}
                                />
                            </button>
                            Include surrounding areas
                        </label>
                    </div>

                    {/* `relative mb-10` is the anchor the Manual Search panel positions itself under. */}
                    <div className="relative mb-10 mt-3 flex flex-col gap-3 sm:flex-row">
                        <form
                            className="flex flex-1 items-center gap-2 rounded-md bg-white p-2 pl-4 shadow-sm"
                            onSubmit={(e) => {
                                e.preventDefault();
                                runSearch();
                            }}
                        >
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by suburb, postcode, street or address"
                                aria-label="Search by suburb, postcode, street or address"
                                className="min-w-0 flex-1 border-none bg-transparent py-2 text-sm sm:text-base text-gray-700 outline-none placeholder:text-gray-400 focus:ring-0"
                            />
                            {query ? (
                                <button
                                    type="button"
                                    onClick={() => setQuery("")}
                                    aria-label="Clear search"
                                    className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            ) : null}
                            <button
                                type="button"
                                onClick={() => setShowFilters((v) => !v)}
                                className="relative flex shrink-0 items-center gap-2 rounded-md border border-[#55C5D0] px-3 py-2 text-xs sm:text-sm font-semibold text-[#2b8e97] hover:bg-[#E8F7F8]"
                            >
                                <SlidersHorizontal className="h-4 w-4" />
                                {showFilters ? "Hide Filters" : "Show Filters"}
                                {activeFilterChips.length ? (
                                    <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#55C5D0] text-[10px] font-bold text-white">
                                        {activeFilterChips.length}
                                    </span>
                                ) : null}
                            </button>
                        </form>
                        <button
                            type="button"
                            onClick={() => runSearch()}
                            className="flex h-12 sm:h-auto shrink-0 items-center justify-center gap-2 rounded-md bg-[#55C5D0] px-10 text-base font-semibold text-white shadow-sm hover:bg-[#45b3be]"
                        >
                            <Search className="h-4 w-4" /> Search
                        </button>

                        <ManualSearchOverlay
                            showFilters={showFilters}
                            setShowFilters={setShowFilters}
                            buyerType="owner"
                            setBuyerType={() => {}}
                            advFilters={advFilters}
                            setAdvFilters={setAdvFilters}
                            filters={filters}
                            setFilters={setFilters}
                            onClear={handleClear}
                            onApply={() => runSearch()}
                            showBuyerType={false}
                            showSaleMethod={false}
                            showPriceOnlyToggle={false}
                            showLocationSearch={false}
                            subtitle="Filter the property database."
                        />
                    </div>
                </div>
            </div>

            {/* Results bar: count, chips, sort, map */}
            <div className="border-b border-gray-100 bg-white px-4 sm:px-6 lg:px-8 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-gray-600">
                        {loading ? (
                            "Searching…"
                        ) : (
                            <>
                                <span className="font-bold text-gray-900">{count.toLocaleString()}</span>{" "}
                                {tab === "all" ? "properties" : `${tabLabel} properties`}
                                {applied.query ? (
                                    <>
                                        {" "}for <span className="font-semibold text-gray-800">"{applied.query}"</span>
                                        {applied.surrounding ? " and surrounding areas" : ""}
                                    </>
                                ) : databaseTotal ? (
                                    <span className="text-gray-400"> · {Number(databaseTotal).toLocaleString()} in the database</span>
                                ) : null}
                            </>
                        )}
                    </p>
                    <div className="flex items-center gap-2">
                        <select
                            value={sort}
                            onChange={handleSort}
                            aria-label="Sort results"
                            className="h-9 rounded-full border border-gray-200 bg-white pl-3 pr-8 text-sm font-semibold text-[#36423F] focus:border-[#55C5D0] focus:ring-0"
                        >
                            {SORTS.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => setShowMap((v) => !v)}
                            className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-bold text-[#36423F] hover:border-[#55C5D0] hover:text-[#55C5D0]"
                        >
                            <MapIcon className="h-4 w-4" />
                            {showMap ? "Hide map" : "Show map"}
                        </button>
                    </div>
                </div>

                {totals && !loading ? (
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        {TABS.filter((t) => t.id !== "all").map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => handleTab(t.id)}
                                className={`hover:text-[#2b8e97] ${tab === t.id ? "font-bold text-[#2b8e97]" : ""}`}
                            >
                                {t.label} {Number(totals[t.id] || 0).toLocaleString()}
                            </button>
                        ))}
                    </div>
                ) : null}

                {activeFilterChips.length ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-gray-400">Filtered by:</span>
                        {activeFilterChips.map((chip) => (
                            <button
                                key={chip.key}
                                type="button"
                                onClick={chip.clear}
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#55C5D0]/40 bg-[#E8F7F8] py-1 pl-3 pr-2 text-xs font-bold text-[#2b8e97] hover:bg-[#d9f1f3]"
                            >
                                {chip.label}
                                <X className="h-3.5 w-3.5" />
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={handleClear}
                            className="ml-1 text-xs font-semibold text-gray-500 underline underline-offset-2 hover:text-[#55C5D0]"
                        >
                            Clear all
                        </button>
                    </div>
                ) : null}
            </div>

            {/* List, with the map beside it when switched on */}
            <div className="flex flex-col lg:flex-row" style={{ height: "calc(100vh - 120px)", minHeight: 480 }}>
                <div
                    ref={listRef}
                    className={`h-full w-full overflow-y-auto ${showMap ? "lg:w-[46%] xl:w-[42%] lg:border-r border-gray-100" : ""}`}
                >
                    <div className="p-4 sm:p-5">
                        {loading ? (
                            <div className="space-y-4">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="flex gap-3 overflow-hidden rounded-xl border border-gray-200 bg-white animate-pulse">
                                        <div className="h-28 w-32 bg-gray-200" />
                                        <div className="flex-1 space-y-2 p-3">
                                            <div className="h-4 w-1/3 rounded bg-gray-200" />
                                            <div className="h-4 w-3/4 rounded bg-gray-200" />
                                            <div className="h-4 w-1/2 rounded bg-gray-200" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : error ? (
                            <div className="py-12 text-center">
                                <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-6">
                                    <p className="font-medium text-red-600">{error}</p>
                                    <button onClick={() => runSearch()} className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700">
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        ) : results.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <Database className="mb-4 h-14 w-14 text-gray-300" />
                                <span className="mb-1 text-lg font-bold text-gray-900">No properties found</span>
                                <p className="max-w-sm text-sm text-gray-400">
                                    {databaseTotal === 0
                                        ? "The property profiles have not been built yet."
                                        : activeFilterChips.length
                                        ? "Nothing matched these filters. Try removing one, or turn on Include surrounding areas."
                                        : tab !== "all" && totals?.all
                                        ? `None of these are ${tabLabel}. All Properties has ${Number(totals.all).toLocaleString()}.`
                                        : "Try a different suburb, postcode or street, or Include surrounding areas."}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className={`grid gap-4 ${showMap ? "grid-cols-1" : "grid-cols-1 xl:grid-cols-2"}`}>
                                    {results.map((p) => {
                                        const selected = selectedId === p.id;
                                        const TypeIcon = TYPE_ICONS[p.property_type] || Home;
                                        const kindColor = KIND_COLORS[p.kind] || KIND_COLORS.not_for_sale;
                                        return (
                                            <div
                                                key={p.id}
                                                ref={(el) => (cardRefs.current[p.id] = el)}
                                                onClick={() => handleCardClick(p)}
                                                className={`flex cursor-pointer overflow-hidden rounded-xl border bg-white transition-shadow ${
                                                    selected ? "border-[#55C5D0] shadow-md ring-1 ring-[#55C5D0]" : "border-gray-200 hover:shadow-lg"
                                                }`}
                                            >
                                                <div
                                                    className="relative flex w-24 sm:w-32 shrink-0 items-center justify-center"
                                                    style={{ backgroundColor: `${kindColor}1A` }}
                                                >
                                                    {p.images?.[0]?.file ? (
                                                        <img
                                                            src={p.images[0].file}
                                                            alt=""
                                                            loading="lazy"
                                                            className="absolute inset-0 h-full w-full object-cover"
                                                            onError={(e) => (e.currentTarget.style.display = "none")}
                                                        />
                                                    ) : null}
                                                    <TypeIcon className="h-9 w-9" style={{ color: kindColor }} />
                                                    <span
                                                        className="absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                                                        style={{ backgroundColor: kindColor }}
                                                    >
                                                        {KIND_LABELS[p.kind] || p.kind_label}
                                                    </span>
                                                </div>
                                                <div className="min-w-0 flex-1 p-3 sm:p-4">
                                                    <p className="text-base font-bold text-gray-900">
                                                        {p.price_display || (p.kind === "buy" || p.kind === "rent" ? "Contact agent" : "No price on record")}
                                                    </p>
                                                    <p className="mt-0.5 line-clamp-1 text-sm font-medium text-gray-800">{p.address}</p>
                                                    {p.owner_contact_count ? (
                                                        // The office has the owner on file (Monday 13354971743); View shows them.
                                                        <span className="mt-1 inline-flex items-center gap-1 rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">
                                                            <UserRound className="h-3 w-3" />
                                                            {p.owner_contact_count === 1 ? "Owner on file" : `${p.owner_contact_count} contacts on file`}
                                                        </span>
                                                    ) : null}
                                                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                                                        <span className="flex items-center gap-1"><Bed className="h-4 w-4" /> {p.beds || 0}</span>
                                                        <span className="flex items-center gap-1"><Bath className="h-4 w-4" /> {p.baths || 0}</span>
                                                        <span className="flex items-center gap-1"><Car className="h-4 w-4" /> {p.parkings || 0}</span>
                                                        {p.land_size ? (
                                                            <span className="flex items-center gap-1"><Ruler className="h-4 w-4" /> {Math.round(p.land_size)} m²</span>
                                                        ) : null}
                                                        {p.property_type ? <span className="text-xs capitalize text-gray-400">{p.property_type}</span> : null}
                                                    </div>
                                                    <p className="mt-1.5 text-xs text-gray-500">
                                                        {p.date_label}
                                                        {p.agency_name ? <span className="text-gray-400"> · {p.agency_name}</span> : null}
                                                    </p>
                                                    <div className="mt-3 flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openDetail(p);
                                                            }}
                                                            className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-200"
                                                        >
                                                            <Eye className="h-3.5 w-3.5" /> View
                                                        </button>
                                                        {p.listing_url ? (
                                                            <a
                                                                href={p.listing_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="flex items-center gap-1 rounded-lg bg-[#E8F7F8] px-3 py-1.5 text-xs text-[#2b8e97] hover:bg-[#d9f1f3]"
                                                            >
                                                                <ExternalLink className="h-3.5 w-3.5" /> Listing
                                                            </a>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div ref={sentinelRef} className="h-1" />
                                {hasMore ? (
                                    <div className="flex justify-center py-5">
                                        {loadingMore ? (
                                            <span className="flex items-center gap-2 text-sm text-gray-500">
                                                <Loader2 className="h-4 w-4 animate-spin text-[#55C5D0]" /> Loading more…
                                            </span>
                                        ) : (
                                            <button onClick={loadMore} className="text-sm font-semibold text-[#55C5D0] hover:underline">
                                                Load more
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <p className="py-5 text-center text-xs text-gray-400">
                                        That's every property for this search.
                                        {lastSynced ? ` Listings last refreshed ${new Date(lastSynced).toLocaleDateString("en-AU")}.` : ""}
                                    </p>
                                )}
                                <p className="pb-4 text-center text-[11px] text-gray-400">{GNAF_CREDIT}</p>
                            </>
                        )}
                    </div>
                </div>

                {showMap ? (
                    <div className="relative hidden h-full flex-1 lg:block">
                        {!MAPBOX_TOKEN ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#EEF2F6] p-6 text-center">
                                <MapPin className="mb-3 h-10 w-10 text-gray-300" />
                                <p className="text-sm font-semibold text-[#36423F]">Map unavailable</p>
                            </div>
                        ) : (
                            <PropertyMapView
                                accessToken={MAPBOX_TOKEN}
                                properties={mapProperties}
                                fitTrigger={fitTrigger}
                                onSelect={handleMarkerSelect}
                                selectedId={selectedId}
                            />
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default CollingsDataPropertiesPage;
